import { useQuery } from '@tanstack/react-query'
import { useCurrentOrgId } from '@/stores/authStore'

import request, { type ComplexResponseType } from './request'
import {
  zodMemberIndividualStatusValidator,
  zodOrganizationMemberResponseValidator,
  type AssignmentStatusType,
  type MemberIndividualStatus,
  type OrganizationMemberResponse
} from './types/Organization'
import { zodAssignedSubjectResponseValidator, type AssignedSubjectResponse } from './types/Subject'

/* ---------------------------- Organization Members ---------------------------- */

const getOrganizationMembers = async ({
  page,
  limit,
  filters,
}: {
  page: number
  limit: number
  filters?: Record<string, string>
}) => {
  const queryParams: string[] = []
  if (page > 1) queryParams.push('page=' + page)
  if (limit > 1) queryParams.push('limit=' + limit)
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value) queryParams.push(`${key}=${value}`)
  })

  const res = await request<ComplexResponseType<OrganizationMemberResponse[]>>({
    endpoint: `/organization/members?${queryParams.join('&')}`,
    method: 'GET',
  })
  if (res.data?.items?.length) zodOrganizationMemberResponseValidator(res.data.items)
  return res
}

export const useGetOrganizationMembers = ({
  page,
  limit,
  filters,
}: {
  page: number
  limit: number
  filters?: Record<string, string>
}, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'members', page, limit, filters],
    queryFn: () => getOrganizationMembers({ page, limit, filters }),
    enabled,
  })
}

const getOrganizationMember = async ({
  userId,
}: {
  userId?: string
}) => {
  const res = await request<OrganizationMemberResponse>({
    endpoint: `/organization/members/${userId}`,
    method: 'GET',
  })
  if (res.success) zodOrganizationMemberResponseValidator([res.data])
  return res
}

export const useGetOrganizationMember = (userId?: string) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'members', userId],
    queryFn: () => getOrganizationMember({ userId }),
  })
}

export const updateOrganizationMember = async (memberId: string, body: any) => {
  return request<any>({
    endpoint: `/organization/members/${memberId}`,
    method: 'PATCH',
    body,
    multipart: true,
  })
}

export const createOrganizationMember = async (body: any) => {
  return request<any>({
    endpoint: '/organization/members',
    method: 'POST',
    body,
    multipart: true,
  })
}

export const deleteOrganizationMember = async (memberId: string) => {
  return request<any>({
    endpoint: `/organization/members/${memberId}`,
    method: 'DELETE',
  })
}

export const changeOrganizationMemberEmail = async (memberId: string, body: { email: string }) => {
  return request<void>({
    endpoint: `/organization/members/${memberId}/email`,
    method: 'PATCH',
    body,
  })
}

export const uploadOrganizationMembersCSV = async (body: any) => {
  return request<any>({
    endpoint: '/organization/members/upload',
    method: 'POST',
    body,
    multipart: true,
  })
}

/* ------- Member Assignments ------- */

const getMemberAssignments = async ({
  memberId,
  page,
  limit,
  filterBy,
  status,
}: {
  memberId: string
  page: number
  limit: number
  filterBy?: string
  status?: AssignmentStatusType
}) => {
  const queryParams: string[] = []
  if (page > 1) queryParams.push('page=' + page)
  if (limit > 1) queryParams.push('limit=' + limit)
  if (filterBy) queryParams.push(`filter[name]=${filterBy}`)
  if (status) queryParams.push(`status=${status}`)

  const res = await request<ComplexResponseType<AssignedSubjectResponse[]>>({
    endpoint: `/organization/members/${memberId}/assignments?${queryParams.join('&')}`,
    method: 'GET',
  })
  if (res.data?.items?.length) zodAssignedSubjectResponseValidator(res.data.items)
  return res
}

export const useGetMemberAssignments = ({
  memberId,
  page,
  limit,
  filterBy,
  status,
}: {
  memberId: string | undefined
  page: number
  limit: number
  filterBy?: string
  status?: AssignmentStatusType
},
  enabled = true
) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'members', memberId, 'assignments', page, limit, filterBy, status],
    queryFn: () =>
      getMemberAssignments({
        memberId: memberId || '',
        page,
        limit,
        filterBy,
        status,
      }),
    enabled: !!memberId && enabled,
  })
}

export const createMemberAssignment = async (
  memberId: string,
  body: any,
  isMultipart = false
) => {
  return request<any>({
    endpoint: `/organization/members/${memberId}/assignments`,
    method: 'POST',
    body,
    multipart: isMultipart,
  })
}

export const deleteMemberAssignment = async (
  memberId: string,
  body: any
) => {
  return request<any>({
    endpoint: `/organization/members/${memberId}/assignments`,
    method: 'DELETE',
    body,
  })
}

export const deleteAllMemberAssignments = async (memberId: string) => {
  return request<any>({
    endpoint: `/organization/members/${memberId}/assignments/all`,
    method: 'DELETE',
  })
}

const getMemberIndividualStatus = async (memberId: string) => {
  const res = await request<MemberIndividualStatus>({
    endpoint: `/organization/members/${memberId}/create-individual`,
    method: 'GET',
  })
  if (res.data) zodMemberIndividualStatusValidator(res.data)
  return res
}

export const useGetMemberIndividualStatus = (memberId: string | undefined) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'members', memberId, 'individual-status'],
    queryFn: () => getMemberIndividualStatus(memberId || ''),
    enabled: !!memberId,
  })
}

export const createMemberIndividualProfile = async (memberId: string) => {
  return request<any>({
    endpoint: `/organization/members/${memberId}/create-individual`,
    method: 'POST',
  })
}
